/**
 * Due diligence — the checklist, grouped by what it is about.
 *
 * ── `unknown` is the useful state ────────────────────────────────────────────
 *
 * A checklist whose only states were pass and fail would quietly turn "nobody
 * has looked at the floor" into "the floor is fine". `unknown` is the state
 * every item starts in, and a BLOCKING item that is still unknown is as
 * important as one that failed — it is the thing standing between here and a
 * decision. The summary counts both, separately, because they need different
 * actions: one is a question to ask, the other is a reason to walk.
 *
 * ── Bilingual at the row level ───────────────────────────────────────────────
 *
 * Each item carries `label_en`/`label_es` and `question_en`/`question_es` from
 * the template, so the checklist is genuinely bilingual in the database rather
 * than in a translation layer bolted on afterwards. The item's own Spanish is
 * preferred; English is the fallback when a template row has not been
 * translated yet.
 */

import { useCallback } from 'react'
import { useLocale, type Locale } from '../../lib/i18n'
import { dateText, TONES } from '../../lib/format'
import { DILIGENCE_CATEGORIES, diligenceCategoryLabel, diligenceStatusLabel } from '../../lib/domain'
import { Chip, Empty, Panel, S } from './ui'
import type { DiligenceItem, DiligenceView } from './types'

const EN = {
  title: 'Due diligence',
  unavailable: 'The checklist could not be loaded.',
  none: 'No checklist has been created for this one yet.',
  noneWhy: 'A checklist is instantiated from the template that matches the structure type.',
  blocking: 'Blocking',
  askSeller: 'Ask the seller',
  asked: 'Asked',
  answered: 'Answered',
  openBlockers: 'blocking answers still missing',
  failedBlockers: 'blocking checks failed',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Investigación',
  unavailable: 'No se pudo cargar la lista.',
  none: 'Todavía no se ha creado una lista para esto.',
  noneWhy: 'La lista se crea de la plantilla que corresponde al tipo de estructura.',
  blocking: 'Bloquea',
  askSeller: 'Preguntar al vendedor',
  asked: 'Preguntado',
  answered: 'Contestado',
  openBlockers: 'respuestas que bloquean todavía faltan',
  failedBlockers: 'verificaciones que bloquean fallaron',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

const STATUS_TONE = (status: string) =>
  status === 'pass' ? TONES.strong : status === 'fail' ? TONES.danger : status === 'na' ? TONES.quiet : TONES.warn

const labelOf = (item: DiligenceItem, locale: Locale): string =>
  (locale === 'es-PR' ? item.label_es : item.label_en) || item.label_en || item.item_key

const questionOf = (item: DiligenceItem, locale: Locale): string | null =>
  (locale === 'es-PR' ? item.question_es : item.question_en) ?? item.question_en

export default function DiligenceList({
  diligence,
  failed = false,
}: {
  diligence: DiligenceView | null
  failed?: boolean
}) {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])

  const items = diligence?.items ?? []
  const summary = diligence?.summary
  const blocked = (summary?.blockingFail ?? 0) + (summary?.blockingUnknown ?? 0)

  // Category order comes from the vocabulary, not from the rows, so a category
  // with nothing in it simply does not appear rather than reordering the panel.
  const groups = DILIGENCE_CATEGORIES.map((category) => ({
    category,
    rows: items.filter((item) => item.category === category),
  })).filter((group) => group.rows.length > 0)

  const ungrouped = items.filter((item) => !(DILIGENCE_CATEGORIES as readonly string[]).includes(item.category))

  return (
    <Panel
      id="diligence"
      title={t('title')}
      defaultOpen={blocked > 0}
      badge={
        summary ? (
          <Chip tone={blocked > 0 ? TONES.warn : TONES.strong}>
            {summary.pass}/{summary.total}
          </Chip>
        ) : undefined
      }
    >
      {failed ? (
        <Empty>{t('unavailable')}</Empty>
      ) : items.length === 0 ? (
        <>
          <Empty>{t('none')}</Empty>
          <p style={S.note}>{t('noneWhy')}</p>
        </>
      ) : (
        <>
          {summary && (summary.blockingUnknown > 0 || summary.blockingFail > 0) && (
            <div style={{ ...S.chipRow, marginBottom: 8 }}>
              {summary.blockingFail > 0 && (
                <Chip tone={TONES.danger}>
                  {summary.blockingFail} {t('failedBlockers')}
                </Chip>
              )}
              {summary.blockingUnknown > 0 && (
                <Chip tone={TONES.warn}>
                  {summary.blockingUnknown} {t('openBlockers')}
                </Chip>
              )}
            </div>
          )}

          {groups.map((group) => (
            <div key={group.category} style={{ marginTop: 12 }}>
              <h3 style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 2px' }}>
                {diligenceCategoryLabel(group.category, locale)}
              </h3>
              {group.rows.map((item) => (
                <ItemRow key={item.id} item={item} locale={locale} t={t} />
              ))}
            </div>
          ))}

          {ungrouped.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {ungrouped.map((item) => (
                <ItemRow key={item.id} item={item} locale={locale} t={t} />
              ))}
            </div>
          )}
        </>
      )}
    </Panel>
  )
}

function ItemRow({ item, locale, t }: { item: DiligenceItem; locale: Locale; t: (key: CopyKey) => string }) {
  const question = questionOf(item, locale)
  return (
    <div style={{ padding: '9px 0', borderTop: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{labelOf(item, locale)}</span>
        <Chip tone={STATUS_TONE(item.status)} outlined={item.status === 'unknown'}>
          {diligenceStatusLabel(item.status, locale)}
        </Chip>
      </div>

      {item.answer && <p style={S.sentence}>{item.answer}</p>}
      {!item.answer && question && <p style={S.note}>{question}</p>}

      <div style={{ ...S.chipRow, marginTop: 6 }}>
        {item.blocking === 1 && <Chip tone={TONES.danger} outlined>{t('blocking')}</Chip>}
        {item.ask_seller === 1 && item.status === 'unknown' && (
          <Chip tone={TONES.quiet} outlined>
            {t('askSeller')}
          </Chip>
        )}
        {item.asked_at && (
          <Chip tone={TONES.quiet} outlined>
            {t('asked')} · {dateText(item.asked_at, locale)}
          </Chip>
        )}
        {item.answered_at && (
          <Chip tone={TONES.quiet} outlined>
            {t('answered')} · {dateText(item.answered_at, locale)}
          </Chip>
        )}
      </div>
    </div>
  )
}
